<script setup>
import { computed } from 'vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  itemDetalhes: { type: Object, required: true },
  listas: { type: Object, required: true },
  formatarValor: { type: Function, default: (v) => v },
  getTextoLista: { type: Function, default: (l, id) => id || '-' },
  formatarMoedaComSigla: { type: Function, default: (v, m) => `${v} ${m}` }
})

// Indica se há condições de pagamento de exportação
const temCondicoesExportacao = computed(() => {
  return props.itemDetalhes.condicoesPagamento &&
    props.itemDetalhes.condicoesPagamento.length > 0
})

// Indica se há condição nacional
const temCondicaoNacional = computed(() => {
  return !!props.itemDetalhes.nomeCondicaoPagamento
})

// Indica se há valor sob análise de crédito
const temValorSobAnalise = computed(() => {
  return props.itemDetalhes.valorSobAnaliseCredito != null &&
    props.itemDetalhes.valorSobAnaliseCredito > 0
})

// Calcula o valor de uma condição com base no percentual
const calcularValorCondicao = (percentual) => {
  const total = props.itemDetalhes.valorTotalNegociado
  if (total == null || percentual == null) return null
  return total * percentual / 100
}

// Classe do badge para requer análise de crédito
const classeRequerCredito = (requer) => {
  return requer
    ? 'badge-ghost-warning'
    : 'badge-ghost-success'
}

const iconeRequerCredito = (requer) => {
  return requer
    ? 'bi-exclamation-triangle'
    : 'bi-check-circle'
}

const textoRequerCredito = (requer) => {
  return requer ? 'Sim' : 'Não'
}
</script>

<template>
  <!-- ============================================================ -->
  <!-- 1️⃣ DADOS DO CLIENTE (linha superior)                        -->
  <!-- ============================================================ -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="fw-bold text-body-emphasis d-block mb-2">
      <i class="bi bi-building me-1 text-info"></i> Dados do Cliente
    </small>

    <div class="row g-2 small">
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1"># Id Parceiro</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.idParceiroCliente || '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Cliente</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.nomeCliente || '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Documento</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.documentoCliente || '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Tipo Doc.</small>
          <span class="fw-medium text-body-emphasis small">{{ getTextoLista(listas.LstTipoDocumento, itemDetalhes.lstTipoDocumentoCliente) }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">E-mail</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.emailCliente || '-' }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- 2️⃣ DADOS DO PEDIDO (linha inferior)                         -->
  <!-- ============================================================ -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="fw-bold text-body-emphasis d-block mb-2">
      <i class="bi bi-receipt me-1 text-info"></i> Dados do Pedido
    </small>

    <div class="row g-2 small">
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Nº Pedido</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.numeroPedidoVenda || '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Moeda</small>
          <span class="fw-medium text-body-emphasis small">{{ getTextoLista(listas.LstMoeda, itemDetalhes.lstMoeda) }}</span>
        </div>
      </div>
      <div class="col-6 col-md-2">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Emissão</small>
          <span class="fw-medium text-body-emphasis small">{{ itemDetalhes.dataEmissao ? formatarDataBR(itemDetalhes.dataEmissao) : '-' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Status do Pedido</small>
          <span class="badge badge-ghost-info">{{ itemDetalhes.nomeStatusWorkflowPedido || getTextoLista(listas.LstStatusWorkflowPedido, itemDetalhes.lstStatusWorkflowPedido) }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="border rounded p-2 h-100 bg-body-tertiary">
          <small class="text-body-secondary d-block lh-1 mb-1">Valor Negociado</small>
          <span class="fw-bold text-body-emphasis small">{{ formatarValor(itemDetalhes.valorTotalNegociado) }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- 3️⃣ OPERAÇÃO FINANCIADA (sempre visível)                     -->
  <!-- ============================================================ -->
  <div class="d-flex align-items-center gap-2 mb-3">
    <span class="text-body-secondary small">Operação Financiada:</span>
    <span v-if="itemDetalhes.operacaoFinanciada" class="badge badge-ghost-warning" style="font-size: 0.85rem;">
      <i class="bi bi-currency-exchange me-1"></i> Sim
    </span>
    <span v-else class="badge badge-ghost-secondary" style="font-size: 0.85rem;">
      <i class="bi bi-check-circle me-1"></i> Não
    </span>
  </div>

  <!-- ============================================================ -->
  <!-- 4️⃣ CONDIÇÕES DE PAGAMENTO × ANÁLISE DE CRÉDITO               -->
  <!-- ============================================================ -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="fw-bold text-body-emphasis d-block mb-2">
      <i class="bi bi-credit-card me-1 text-info"></i> Condições de Pagamento × Análise de Crédito
    </small>

    <!-- Condição Nacional -->
    <div v-if="temCondicaoNacional" class="border rounded bg-body-tertiary px-2 py-1 mb-2 small">
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-bookmark-check me-1 text-info"></i>
        <span class="fw-medium text-body-emphasis">{{ itemDetalhes.nomeCondicaoPagamento }}</span>
        <span class="fw-medium text-body-emphasis ms-2">{{ formatarValor(itemDetalhes.valorTotalNegociado) }}</span>
        <span class="badge ms-auto" :class="classeRequerCredito(itemDetalhes.requerAnaliseCreditoCondicaoNacional)" style="font-size: 0.75rem;">
          <i :class="iconeRequerCredito(itemDetalhes.requerAnaliseCreditoCondicaoNacional) + ' me-1'"></i>
          {{ textoRequerCredito(itemDetalhes.requerAnaliseCreditoCondicaoNacional) }}
        </span>
      </div>
      <div v-if="itemDetalhes.requerAnaliseCreditoCondicaoNacional" class="text-body-secondary small mt-1">
        <i class="bi bi-info-circle me-1"></i>
        Condição com entrada e/ou parcelamento — requer análise de crédito.
      </div>
      <div v-else class="text-body-secondary small mt-1">
        <i class="bi bi-info-circle me-1"></i>
        Condição à vista com parcela única — não requer análise de crédito.
      </div>
    </div>

    <!-- Condições de Exportação (tabela) -->
    <div v-if="temCondicoesExportacao" class="table-responsive itens-scroll-wrapper">
      <table class="table table-sm table-hover align-middle mb-0">
        <thead class="thead-tema">
          <tr>
            <th style="font-size: 0.75rem;">#</th>
            <th class="text-end" style="font-size: 0.75rem;">%</th>
            <th class="text-end" style="font-size: 0.75rem;">Valor</th>
            <th class="text-end" style="font-size: 0.75rem;">Prazo</th>
            <th style="font-size: 0.75rem;">Referência</th>
            <th style="font-size: 0.75rem;">Momento</th>
            <th class="text-center" style="font-size: 0.75rem;">Requer Crédito?</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cond, idx) in itemDetalhes.condicoesPagamento" :key="idx">
            <td class="small">{{ idx + 1 }}</td>
            <td class="text-end small">{{ props.formatarValor(cond.percentualDoTotal) }}%</td>
            <td class="text-end small">{{ formatarValor(calcularValorCondicao(cond.percentualDoTotal)) }}</td>
            <td class="text-end small">{{ cond.prazoDias }} dias</td>
            <td class="small">{{ getTextoLista(listas.LstReferenciaTemporal, cond.lstReferenciaTemporal) }}</td>
            <td class="small">{{ getTextoLista(listas.LstCondPgMomento, cond.lstCondPgMomento) }}</td>
            <td class="text-center">
              <span class="badge" :class="classeRequerCredito(cond.requerAnaliseCredito)" style="font-size: 0.7rem;">
                <i :class="iconeRequerCredito(cond.requerAnaliseCredito) + ' me-1'"></i>
                {{ textoRequerCredito(cond.requerAnaliseCredito) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!temCondicaoNacional && !temCondicoesExportacao" class="text-center text-body-secondary small py-2">
      <i class="bi bi-info-circle me-1"></i> Nenhuma condição de pagamento registrada.
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- 5️⃣ TOTAL SOB ANÁLISE DE CRÉDITO + RESUMO                     -->
  <!-- ============================================================ -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <div class="row g-2 small">
      <!-- Total sob análise -->
      <div class="col-md-6" v-if="temValorSobAnalise">
        <div class="border rounded p-2 h-100" style="background-color: var(--bs-warning-bg-subtle);">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-exclamation-triangle text-warning-emphasis fs-5"></i>
            <div>
              <small class="text-body-secondary d-block lh-1">Valor sob Análise de Crédito</small>
              <span class="fw-bold text-body-emphasis">{{ formatarValor(itemDetalhes.valorSobAnaliseCredito) }}</span>
              <small class="text-body-secondary d-block">
                de {{ formatarValor(itemDetalhes.valorTotalNegociado) }} negociados
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumo da solicitação -->
      <div class="col-md-6">
        <div class="d-flex align-items-start gap-2" v-if="itemDetalhes.nomeSolicitante">
          <i class="bi bi-person-fill text-info"></i>
          <div>
            <small class="text-body-secondary d-block lh-1">Solicitante</small>
            <span class="fw-medium text-body-emphasis">{{ itemDetalhes.nomeSolicitante }}</span>
          </div>
        </div>
        <div class="d-flex align-items-start gap-2 mt-1">
          <i class="bi bi-chat-quote text-info"></i>
          <div>
            <small class="text-body-secondary d-block lh-1">Resumo</small>
            <span class="text-body-secondary">{{ itemDetalhes.resumoSolicitacao || 'Nenhum resumo registrado.' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.itens-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
}

.itens-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}

.itens-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}

/* Header de tabela adaptável ao tema (dark/light) — usa variáveis semânticas */
:deep(.thead-tema) {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}
</style>
